import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useAgentMode } from "@/hooks/useAgentMode";
import { useAgentOrgActions } from "@/hooks/useAgentOrgActions";
import CompareTray from "@/components/explore/CompareTray";

/**
 * Single app-level mount for the CompareTray, so the compare set surfaces on
 * every agent surface — Explore, the map, and org profiles — rather than only
 * the Explore page that used to own it. Follows the AgentOnboardingGate pattern:
 * an always-mounted component that self-gates and renders nothing when it
 * shouldn't appear.
 *
 * Gate: the agent experience only. `isAgent || (isAdmin && isAgentMode)` is the
 * same definition useOnboardingStatus uses for "acting as an agent right now."
 * isAgentMode is force-false on /admin and /org routes (useAgentMode), so org
 * admins never see the tray; signed-out/public visitors have no user. Agents
 * compare orgs; org-admins do not (CLAUDE.md). The tray itself returns null when
 * the set is empty, so it only paints once something is selected.
 *
 * It stays off the comparison itself, where it only repeats the page, and off
 * the agent's own profile and settings, where it sat on top of form fields
 * (Joe, 2026-10-13). The compare set is untouched on those pages.
 */
const HIDDEN_ON = ["/compare", "/profile", "/settings"];

const CompareTrayMount = () => {
  const { user } = useAuth();
  const { isAgent, isAdmin } = useUserRole();
  const { isAgentMode } = useAgentMode();
  const { compareItems, clearCompare } = useAgentOrgActions();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isAgentExperience = !!user && (isAgent || (isAdmin && isAgentMode));
  // Each of these has an /agent/ twin (App.tsx).
  if (!isAgentExperience || HIDDEN_ON.includes(pathname.replace(/^\/agent(?=\/)/, ""))) return null;

  const handleCompare = () => {
    if (compareItems.length < 2) return; // the tray CTA is disabled below 2 anyway
    navigate(`/compare?ids=${compareItems.map((o) => o.id).join(",")}`);
  };

  return <CompareTray picked={compareItems} onClear={clearCompare} onCompare={handleCompare} />;
};

export default CompareTrayMount;
